'use client'
import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FiHardDrive, FiDatabase, FiArrowRight, FiFileText, FiLayout, FiCheckCircle, FiActivity, FiGlobe, FiCpu, FiServer, FiZap, FiWifi, FiAlertTriangle } from 'react-icons/fi'
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import Link from 'next/link'
import Swal from 'sweetalert2'

export default function HostingOverviewPage() {
  const [account, setAccount] = useState<any>(null)
  const [plan, setPlan] = useState<any>(null)
  const [loading, setLoading] = useState(false)
  
  // Métricas del servidor reales (via API)
  const [metrics, setMetrics] = useState({
    ramPercent: 0,
    totalRAM: 16,
    usedRAM: 0,
    traffic: 154.2,
    latency: 18
  })

  useEffect(() => {
    fetchAccount()
    fetchRealMetrics()
    
    // Actualizar métricas reales y simular variaciones leves
    const interval = setInterval(() => {
      fetchRealMetrics()
      setMetrics(prev => ({
        ...prev,
        traffic: prev.traffic + (Math.random() * 0.01),
        latency: Math.min(Math.max(prev.latency + (Math.random() * 2 - 1), 10), 20)
      }))
    }, 5000)

    return () => clearInterval(interval)
  }, [])

  const fetchRealMetrics = async () => {
    try {
      const res = await fetch('/api/system/metrics')
      const data = await res.json()
      if (data.success) {
        setMetrics(prev => ({
          ...prev,
          ramPercent: parseFloat(data.metrics.ramPercent),
          totalRAM: parseInt(data.metrics.totalRAM),
          usedRAM: parseFloat(data.metrics.usedRAM)
        }))
      }
    } catch (e) {
      console.error("Error fetching real metrics:", e)
    }
  }

  const fetchAccount = async () => {
    try {
      const res = await fetch('/api/auth/verificar')
      const data = await res.json()
      if (data.success) {
        // Obtenemos la cuenta seleccionada del localStorage si existe
        const savedAccId = localStorage.getItem('selected_hosting_account')
        const accs = data.usuario.hostingAccounts || []
        const currentAcc = accs.find((a: any) => a.id.toString() === savedAccId) || accs[0]
        setAccount(currentAcc)
        if (currentAcc) fetchPlan(currentAcc.id)
      }
    } catch (e) {
      console.error("Error fetching account:", e)
    }
  }

  const fetchPlan = async (accountId: number) => {
    try {
      const res = await fetch(`/api/hosting/plan?accountId=${accountId}`)
      const data = await res.json()
      if (data.success) setPlan(data.plan)
    } catch (e) {
      console.error("Error fetching plan:", e)
    }
  }

  const handleInstallWP = async () => {
    if (!account) return

    const { value: folder } = await Swal.fire({
      title: 'Instalar WordPress',
      html: `
        <div class="text-left py-2">
          <p class="text-sm text-gray-500 mb-4">Se instalará la última versión de WordPress automáticamente.</p>
          <div class="bg-blue-50 p-3 rounded-lg border border-blue-100 mb-4">
            <p class="text-xs text-blue-700 font-bold flex items-center gap-1">
              <FiCheckCircle class="shrink-0" /> SE CREARÁ UNA NUEVA BASE DE DATOS:
            </p>
            <p class="text-xs font-mono mt-1 text-blue-900">wp_${account.dbUser}</p>
          </div>
          <label class="text-xs font-bold text-gray-400 uppercase tracking-widest">¿Dónde quieres instalarlo?</label>
          <div class="mt-2 space-y-2">
            <input id="wp-subfolder" class="w-full px-4 py-2 border rounded-lg text-sm" placeholder="Ej: blog (deja vacío para raíz)">
            <p class="text-[10px] text-amber-600 font-medium">Nota: Si instalas en la raíz, la carpeta debe estar vacía para evitar borrar tu sistema actual.</p>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Empezar Instalación',
      confirmButtonColor: '#2563eb',
      cancelButtonText: 'Cancelar',
      customClass: { popup: 'rounded-2xl' },
      preConfirm: () => (document.getElementById('wp-subfolder') as HTMLInputElement).value
    })

    if (folder !== undefined) {
      setLoading(true)
      Swal.fire({
        title: 'Instalando WordPress...',
        text: 'Estamos descargando y configurando todo. No cierres esta ventana.',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      })

      try {
        const res = await fetch('/api/hosting/installers/wordpress', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ accountId: account.id, subfolder: folder })
        })
        const data = await res.json()
        
        if (data.success) {
          Swal.fire({
            icon: 'success',
            title: '¡Instalado!',
            text: 'WordPress se ha configurado correctamente con su propia base de datos.',
            confirmButtonText: 'Ir al Sitio',
            preConfirm: () => {
              // Limpiar el dominio de protocolos y barras sobrantes
              const cleanDominio = account.dominio.replace(/^https?:\/\//, '').replace(/\/$/, '')
              const cleanFolder = folder.replace(/^\//, '')
              const url = `http://${cleanDominio}/${cleanFolder}`.replace(/\/$/, '')
              window.open(url, '_blank')
            }
          })
        } else {
          Swal.fire('Error', data.error || 'No se pudo instalar WordPress', 'error')
        }
      } catch (err) {
        Swal.fire('Error', 'Ocurrió un error en la conexión', 'error')
      } finally {
        setLoading(false)
      }
    }
  }

  const handleInstallAISite = async () => {
    if (!account) return

    // 1. Preguntar por la descripción del negocio
    const { value: prompt } = await Swal.fire({
      title: 'Constructor Mágico con IA',
      html: `
        <div class="text-left space-y-4">
          <p class="text-xs text-gray-500 italic">Describe tu negocio y la IA diseñará una página web profesional con Tailwind CSS para ti.</p>
          <textarea id="ai-prompt" class="w-full h-32 p-3 border rounded-xl text-sm focus:ring-2 focus:ring-purple-500 outline-none" 
            placeholder="Ej: Una landing page moderna para un taller mecánico en Madrid llamado 'Rockma Motors'. Debe tener secciones de servicios, galería de trabajos y contacto. Colores negro y naranja."
          ></textarea>
          <div class="bg-blue-50 p-3 rounded-lg border border-blue-100">
            <span class="text-[10px] font-bold text-blue-600 uppercase tracking-tighter">💡 Ideas Sugeridas:</span>
            <ul class="text-[10px] text-blue-700 mt-1 space-y-1 list-disc ml-3">
              <li>"Landing page para una pizzería artesanal con menú y reserva."</li>
              <li>"Portafolio para un fotógrafo de bodas minimalista y elegante."</li>
              <li>"Sitio para una agencia de marketing con planes de precios."</li>
            </ul>
          </div>
          <div class="space-y-1">
            <label class="text-[10px] font-bold text-gray-400 uppercase">¿Dónde instalar?</label>
            <input id="ai-subfolder" type="text" class="w-full p-2 border rounded-lg text-sm" placeholder="Ej: landing (dejar vacío para raíz)">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Siguiente: Escanear Carpeta',
      confirmButtonColor: '#7c3aed',
      customClass: { popup: 'rounded-2xl' },
      preConfirm: () => ({
        promptText: (document.getElementById('ai-prompt') as HTMLTextAreaElement).value,
        subfolder: (document.getElementById('ai-subfolder') as HTMLInputElement).value
      })
    })

    if (!prompt || !prompt.promptText) return

    const subfolder = prompt.subfolder;
    
    // 2. Escanear archivos existentes
    Swal.fire({ 
      title: 'Escaneando carpeta...', 
      html: `Revisando <code class="bg-gray-100 px-1 rounded text-purple-600 font-bold">${subfolder || '/'}</code>...`,
      didOpen: () => Swal.showLoading(),
      customClass: { popup: 'rounded-2xl' }
    })
    
    try {
      const resFiles = await fetch(`/api/hosting/files?folder=${account.folderName}&path=${subfolder || ''}`)
      const files = await resFiles.json()

      let filesToDelete: string[] = []

      if (Array.isArray(files) && files.length > 0) {
        const hasIndex = files.some((f: any) => f.name.toLowerCase() === 'index.html')
        
        const { value: selection } = await Swal.fire({
          title: '¡Espera! Carpeta no vacía',
          html: `
            <div class="text-left py-2">
              ${hasIndex ? `
                <div class="bg-amber-50 border border-amber-200 p-3 rounded-xl mb-4 flex gap-3">
                  <span class="text-2xl">⚠️</span>
                  <div class="flex flex-col">
                    <span class="text-xs font-bold text-amber-800 uppercase tracking-tighter">¡Advertencia!</span>
                    <span class="text-xs text-amber-700 leading-tight">Se detectó un <b>index.html</b>. Si no lo borras, la IA lo reemplazará por el nuevo diseño.</span>
                  </div>
                </div>
              ` : ''}
              <div class="flex justify-between items-center mb-2">
                <p class="text-xs text-gray-500">Selecciona qué archivos deseas eliminar:</p>
                <button type="button" onclick="document.querySelectorAll('.file-cb').forEach(cb => cb.checked = false)" class="text-[10px] font-bold text-purple-600 hover:text-purple-800 uppercase tracking-tighter cursor-pointer">Desmarcar todo</button>
              </div>
              <div class="max-h-56 overflow-y-auto border rounded-xl p-2 space-y-1 bg-gray-50/50">
                ${files.map((f: any) => `
                  <label class="flex items-center gap-3 p-3 hover:bg-white rounded-xl transition-all cursor-pointer border border-transparent hover:border-gray-200 group">
                    <input type="checkbox" class="file-cb h-5 w-5 rounded-md border-gray-300 text-purple-600 focus:ring-purple-500 transition-all cursor-pointer" value="${f.name}" ${f.name.toLowerCase() === 'index.html' ? 'checked' : ''}>
                    <div class="flex flex-col flex-1">
                      <span class="text-sm font-semibold text-gray-700 group-hover:text-purple-700 font-mono">${f.name}</span>
                      <span class="text-[10px] text-gray-400 font-medium uppercase">${f.isDirectory ? '📁 Carpeta' : '📄 Archivo'}</span>
                    </div>
                  </label>
                `).join('')}
              </div>
              <p class="mt-2 text-[10px] text-gray-400 italic">Si no marcas nada, la IA se instalará junto a tus archivos actuales (el index.html se reemplazará si existe).</p>
              <div class="mt-4 p-3 bg-gray-100 rounded-lg flex items-center justify-between">
                <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Seguridad MiApi</span>
                <span class="text-[10px] text-amber-600 font-bold uppercase">⚠️ Borrado Permanente</span>
              </div>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: 'Generar Web con IA',
          cancelButtonText: 'Cambiar Carpeta',
          confirmButtonColor: '#7c3aed',
          customClass: { popup: 'rounded-2xl', confirmButton: 'rounded-xl px-6', cancelButton: 'rounded-xl' },
          preConfirm: () => {
            const checkboxes = document.querySelectorAll('.file-cb:checked') as NodeListOf<HTMLInputElement>
            return Array.from(checkboxes).map(cb => cb.value)
          }
        })

        if (selection === undefined) {
          // Si cancela el escaneo, volvemos a preguntar (recursivo simple llamando a la misma función)
          handleInstallAISite()
          return
        }
        filesToDelete = selection
      }

      // 3. Generar con IA
      setLoading(true)
      
      let progress = 0
      let apiAborted = false
      const interval = setInterval(() => {
        if (progress < 90) {
          progress += Math.random() * 5
          if (progress > 90) progress = 90
          Swal.update({
            html: `
              <div class="text-left py-2">
                <p class="text-xs text-gray-500 mb-4 italic">La IA está escribiendo el código, estructurando secciones y buscando imágenes profesionales para ti...</p>
                <div class="w-full bg-gray-100 rounded-full h-4 overflow-hidden border border-gray-200">
                  <div class="bg-gradient-to-r from-purple-500 to-fuchsia-500 h-full transition-all duration-500 ease-out" style="width: ${Math.round(progress)}%"></div>
                </div>
                <div class="flex justify-between mt-2">
                  <span class="text-[10px] font-bold text-gray-400 uppercase tracking-widest">${Math.round(progress)}% Completado</span>
                  <span class="text-[10px] font-bold text-purple-600 animate-pulse uppercase tracking-widest">IA Trabajando...</span>
                </div>
              </div>
            `
          })
        }
      }, 1000)

      // El modal NO se debe esperar (await) antes del fetch, sino que debe "envolver" la operación
      Swal.fire({
        title: 'MiApi.Cloud está diseñando tu web...',
        html: `
          <div class="text-left py-2">
            <p class="text-xs text-gray-500 mb-4 italic">Iniciando el motor de IA...</p>
            <div class="w-full bg-gray-100 rounded-full h-4 overflow-hidden border border-gray-200">
              <div class="bg-gradient-to-r from-purple-500 to-fuchsia-500 h-full" style="width: 0%"></div>
            </div>
          </div>
        `,
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCancelButton: true,
        showConfirmButton: false,
        cancelButtonText: 'Cancelar',
        customClass: { popup: 'rounded-2xl' },
      }).then(async (result) => {
        if (result.isDismissed) {
          apiAborted = true
          clearInterval(interval)
          // Aquí idealmente cancelaríamos la petición fetch si soportara AbortController
        }
      })

      try {
        const resAi = await fetch('/api/hosting/installers/ai-site', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accountId: account.id,
            subfolder,
            prompt: prompt.promptText,
            deleteList: filesToDelete
          })
        })
        
        if (apiAborted) return

        clearInterval(interval)
        const data = await resAi.json()
        
        if (data.success) {
          progress = 100
          Swal.fire({
            icon: 'success',
            title: '¡Web Generada!',
            text: 'Tu sitio ha sido creado y publicado. ¡Se ve increíble!',
            confirmButtonText: 'Ver mi nueva web',
            preConfirm: () => window.open(data.url, '_blank')
          })
        } else {
          Swal.fire('Error', data.error || 'No se pudo generar el sitio', 'error')
        }
      } catch (err) {
        if (apiAborted) return
        clearInterval(interval)
        console.error(err)
        Swal.fire('Error', 'Error de conexión o de servidor', 'error')
      } finally {
        setLoading(false)
      }
    } catch (err) {
      console.error(err)
      Swal.fire('Error', 'Error al escanear carpeta', 'error')
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-gray-900">Mi Hosting</h1>
        <p className="text-gray-500">Gestiona tus archivos y aplicaciones de un vistazo.</p>
      </div>

      {plan?.tieneFacturaPendiente && (
        <Link href="/hosting/plan" className="block">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-5 py-4 hover:bg-red-100/70 transition-colors">
            <FiAlertTriangle className="h-5 w-5 text-red-600 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-bold text-red-700">
                {plan.estado === 'VENCIDO' || plan.estado === 'SUSPENDIDO'
                  ? 'Tu hosting ha vencido'
                  : 'Tu hosting esta por vencer'}
              </p>
              <p className="text-xs text-red-600">
                Tienes una factura pendiente de {plan.moneda === 'PEN' ? 'S/' : plan.moneda} {Number(plan.montoPendiente).toFixed(2)}. Renueva para evitar el corte del servicio.
              </p>
            </div>
            <span className="self-start sm:self-auto rounded-xl bg-red-600 px-4 py-2 text-xs font-bold text-white shadow-lg shadow-red-200">
              Ir a Mi Plan
            </span>
          </div>
        </Link>
      )}

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="bg-gray-100/50 p-1 rounded-xl mb-4">
          <TabsTrigger value="overview" className="rounded-lg px-6 py-2">Resumen</TabsTrigger>
          <TabsTrigger value="apps" className="rounded-lg px-6 py-2 flex items-center gap-2">
            <FiLayout className="h-4 w-4" /> Tienda de Apps
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="hover:shadow-md transition-shadow border-none shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Archivos</CardTitle>
                <FiHardDrive className="h-4 w-4 text-blue-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900 truncate">/{account?.folderName || '---'}</div>
                <p className="text-xs text-gray-500 mt-1">Directorio principal en el servidor</p>
                <Link href="/hosting/files">
                  <Button variant="outline" size="sm" className="mt-4 w-full gap-2">
                    Explorar Archivos <FiArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              </CardContent>
            </Card>

            <Card className="hover:shadow-md transition-shadow border-none shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">Base de Datos</CardTitle>
                <FiDatabase className="h-4 w-4 text-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900 truncate">{account?.dbName || '---'}</div>
                <p className="text-xs text-gray-500 mt-1">MySQL Database asignada</p>
                <Link href="/hosting/databases">
                  <Button variant="outline" size="sm" className="mt-4 w-full gap-2 transition-all hover:bg-green-50">
                    Gestionar Tablas <FiArrowRight className="h-3 w-3" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>

          <Card className="border-none shadow-sm overflow-hidden border border-gray-100">
            <CardHeader className="bg-gray-50/50 border-b">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FiFileText className="text-blue-500" /> Información de Acceso
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1 p-3 bg-slate-50 rounded-xl">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Dominio Principal</span>
                  <span className="font-mono text-sm text-blue-600 font-bold truncate">{account?.dominio || '---'}</span>
                </div>
                <div className="flex flex-col gap-1 p-3 bg-slate-50 rounded-xl">
                  <span className="text-[10px] uppercase font-bold text-gray-400 tracking-widest">Estado del Servicio</span>
                  {plan?.estado === 'VENCIDO' || plan?.estado === 'SUSPENDIDO' ? (
                    <Link href="/hosting/plan" className="flex items-center gap-1.5 text-sm font-bold text-red-600 hover:underline">
                      <span className="h-2 w-2 rounded-full bg-red-500" /> Vencido - Renovar
                    </Link>
                  ) : plan?.estado === 'POR_VENCER' ? (
                    <Link href="/hosting/plan" className="flex items-center gap-1.5 text-sm font-bold text-amber-600 hover:underline">
                      <span className="h-2 w-2 rounded-full bg-amber-500 animate-pulse" /> Por vencer
                    </Link>
                  ) : (
                    <span className="flex items-center gap-1.5 text-sm font-bold text-green-600">
                      <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" /> Activo
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-none shadow-sm overflow-hidden border border-gray-100 group">
            <CardHeader className="bg-gray-50/50 border-b flex flex-row items-center justify-between py-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FiServer className="text-purple-500" /> Estado del Servidor
              </CardTitle>
              <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-purple-100 text-[10px] font-bold text-purple-700 uppercase tracking-tighter">
                <FiActivity className="h-3 w-3 animate-pulse" /> Tiempo Real
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex flex-col gap-1.5 p-3 bg-slate-50 rounded-xl hover:bg-purple-50/30 transition-colors">
                  <span className="text-[9px] uppercase font-bold text-gray-400 tracking-widest flex items-center gap-1">
                    <FiCpu className="text-gray-400" /> RAM (Uso)
                  </span>
                  <div className="space-y-1.5">
                    <div className="flex justify-between items-end">
                      <span className="text-sm font-bold text-purple-700">{metrics.ramPercent}%</span>
                      <span className="text-[10px] text-gray-400 font-medium whitespace-nowrap">{metrics.usedRAM}GB / {metrics.totalRAM}GB</span>
                    </div>
                    <div className="w-full bg-gray-200 h-1 rounded-full overflow-hidden border border-gray-100">
                      <div className="bg-purple-500 h-full transition-all duration-500" style={{ width: `${metrics.ramPercent}%` }} />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 p-3 bg-slate-50 rounded-xl hover:bg-blue-50/30 transition-colors">
                  <span className="text-[9px] uppercase font-bold text-gray-400 tracking-widest flex items-center gap-1">
                    <FiWifi className="text-gray-400" /> Red & Latencia
                  </span>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-blue-600">{metrics.latency.toFixed(0)} ms</span>
                    <span className="text-[9px] px-1.5 py-0.5 bg-green-100 text-green-700 rounded font-bold uppercase tracking-tighter">Excelente</span>
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 p-3 bg-slate-50 rounded-xl hover:bg-green-50/30 transition-colors">
                  <span className="text-[9px] uppercase font-bold text-gray-400 tracking-widest flex items-center gap-1">
                    <FiZap className="text-gray-400" /> Tráfico (Mes)
                  </span>
                  <div className="flex justify-between items-end">
                    <span className="text-sm font-bold text-green-700">{metrics.traffic.toFixed(1)} GB</span>
                    <span className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter">Limitado</span>
                  </div>
                  <div className="w-full bg-gray-200 h-1 rounded-full overflow-hidden border border-gray-100">
                    <div className="bg-green-500 h-full" style={{ width: `${(metrics.traffic / 1000) * 100}%` }} />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 p-3 bg-slate-50 rounded-xl hover:bg-orange-50/30 transition-colors">
                  <span className="text-[9px] uppercase font-bold text-gray-400 tracking-widest flex items-center gap-1">
                    <FiArrowRight className="text-gray-400 rotate-[-45deg]" /> Velocidad 
                  </span>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-orange-600">1 Gbps</span>
                    <div className="flex gap-0.5">
                      {[1,2,3,4].map(i => <div key={i} className="h-1.5 w-0.5 bg-orange-200 rounded-full" style={{ backgroundColor: i <= 3 ? '#ea580c' : '' }} />)}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="apps" className="animate-in slide-in-from-bottom-2 duration-300">
          <div className="grid gap-6 md:grid-cols-3">
            <Card className="flex flex-col overflow-hidden border-none shadow-sm hover:shadow-md transition-all group">
              <div className="h-24 bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center relative overflow-hidden">
                <FiGlobe className="h-12 w-12 text-white/20 absolute -right-2 -bottom-2 rotate-12" />
                <div className="bg-white p-3 rounded-2xl shadow-xl z-10 transition-transform group-hover:scale-110">
                  <FiArrowRight className="h-6 w-6 text-blue-600 -rotate-45" />
                </div>
              </div>
              <CardContent className="pt-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">WordPress</h3>
                  <span className="text-[9px] font-bold bg-green-100 text-green-700 px-2 py-0.5 rounded-full border border-green-200">PHP 8.1</span>
                </div>
                <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                  Crea tu blog o negocio con el CMS más popular del mundo. Incluye DB dedicada.
                </p>
                <div className="mt-auto pt-4">
                  <Button 
                    onClick={handleInstallWP} 
                    disabled={loading}
                    className="w-full gap-2 bg-blue-600 shadow-lg shadow-blue-100 py-5 rounded-xl text-xs font-bold"
                  >
                    <FiArrowRight className="h-3 w-3" /> Instalar Ahora
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="flex flex-col overflow-hidden border-none shadow-sm hover:shadow-md transition-all group">
              <div className="h-24 bg-gradient-to-br from-purple-600 to-fuchsia-700 flex items-center justify-center relative overflow-hidden">
                <FiActivity className="h-12 w-12 text-white/20 absolute -right-2 -bottom-2 rotate-12" />
                <div className="bg-white p-3 rounded-2xl shadow-xl z-10 transition-transform group-hover:scale-110">
                  <FiLayout className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <CardContent className="pt-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-bold text-gray-900 group-hover:text-purple-600 transition-colors">Constructor IA</h3>
                  <span className="text-[9px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full border border-purple-200 uppercase">Flash 1.5</span>
                </div>
                <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                  Genera una web profesional en segundos solo con texto. Diseño moderno y responsivo.
                </p>
                <div className="mt-auto pt-4">
                  <Button 
                    onClick={handleInstallAISite} 
                    disabled={loading}
                    className="w-full gap-2 bg-purple-600 hover:bg-purple-700 shadow-lg shadow-purple-100 py-5 rounded-xl text-xs font-bold transition-all"
                  >
                    <FiArrowRight className="h-3 w-3" /> Construir con IA
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Placeholder para futuras apps */}
            <div className="border-2 border-dashed border-gray-100 rounded-3xl flex flex-col items-center justify-center p-8 text-center bg-gray-50/30">
              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-300 mb-2">
                <FiActivity className="h-5 w-5" />
              </div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest leading-tight">Más aplicaciones<br/>próximamente</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
